/**
 * Report Export Utility
 * Provides functions to export report data to CSV, Excel, and PDF formats
 */

export interface ExportColumn {
  header: string;
  key: string;
  width?: number;
  format?: (value: any) => string;
}

export interface ExportData {
  title: string;
  description?: string;
  columns: ExportColumn[];
  rows: any[];
  metadata?: Record<string, string>;
}

/**
 * Export data to CSV format
 */
export const exportToCSV = (data: ExportData, filename?: string): void => {
  try {
    const { title, columns, rows } = data;
    const csvFilename = filename || `${sanitizeFilename(title)}_${getTimestamp()}.csv`;

    // Create CSV content
    const headers = columns.map(col => escapeCSV(col.header)).join(',');
    const csvRows = rows.map(row =>
      columns.map(col => {
        const value = row[col.key];
        const formatted = col.format ? col.format(value) : value;
        return escapeCSV(String(formatted ?? ''));
      }).join(',')
    );

    const csvContent = [headers, ...csvRows].join('\n');

    // Create and download
    downloadFile(csvContent, csvFilename, 'text/csv;charset=utf-8;');
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    throw new Error('Failed to export to CSV');
  }
};

/**
 * Export data to Excel format (using CSV as Excel-compatible format)
 */
export const exportToExcel = (data: ExportData, filename?: string): void => {
  try {
    const { title, columns, rows, metadata } = data;
    const excelFilename = filename || `${sanitizeFilename(title)}_${getTimestamp()}.xls`;

    // Create Excel-compatible HTML table
    let htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office"
            xmlns:x="urn:schemas-microsoft-com:office:excel"
            xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta http-equiv="content-type" content="application/vnd.ms-excel; charset=UTF-8">
        <style>
          table { border-collapse: collapse; width: 100%; }
          th { background-color: #4472C4; color: white; font-weight: bold; padding: 8px; border: 1px solid #ddd; }
          td { padding: 8px; border: 1px solid #ddd; }
          .title { font-size: 18px; font-weight: bold; margin-bottom: 10px; }
          .metadata { font-size: 12px; color: #666; margin-bottom: 20px; }
        </style>
      </head>
      <body>
        <div class="title">${title}</div>
    `;

    // Add metadata if provided
    if (metadata) {
      htmlContent += '<div class="metadata">';
      Object.entries(metadata).forEach(([key, value]) => {
        htmlContent += `<div>${key}: ${value}</div>`;
      });
      htmlContent += '</div>';
    }

    // Create table
    htmlContent += '<table>';

    // Headers
    htmlContent += '<thead><tr>';
    columns.forEach(col => {
      htmlContent += `<th>${col.header}</th>`;
    });
    htmlContent += '</tr></thead>';

    // Data rows
    htmlContent += '<tbody>';
    rows.forEach(row => {
      htmlContent += '<tr>';
      columns.forEach(col => {
        const value = row[col.key];
        const formatted = col.format ? col.format(value) : value;
        htmlContent += `<td>${formatted ?? ''}</td>`;
      });
      htmlContent += '</tr>';
    });
    htmlContent += '</tbody>';

    htmlContent += '</table></body></html>';

    // Create and download
    downloadFile(htmlContent, excelFilename, 'application/vnd.ms-excel');
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw new Error('Failed to export to Excel');
  }
};

/**
 * Export data to PDF format (using basic HTML-to-PDF approach)
 * Note: For production, consider using a library like jsPDF or pdfmake
 */
export const exportToPDF = (data: ExportData, filename?: string): void => {
  try {
    const { title, description, columns, rows, metadata } = data;
    const pdfFilename = filename || `${sanitizeFilename(title)}_${getTimestamp()}.pdf`;

    // For now, create a printable HTML version
    // In production, you'd use jsPDF or similar library
    const printWindow = window.open('', '', 'width=800,height=600');

    if (!printWindow) {
      throw new Error('Failed to open print window. Please check popup blocker settings.');
    }

    let htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${title}</title>
        <style>
          @media print {
            @page { margin: 1cm; size: A4 landscape; }
            body { margin: 0; }
          }
          body { font-family: Arial, sans-serif; padding: 20px; }
          .header { margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }
          .title { font-size: 24px; font-weight: bold; color: #333; }
          .description { font-size: 14px; color: #666; margin-top: 5px; }
          .metadata { font-size: 12px; color: #666; margin: 10px 0; }
          .metadata-item { display: inline-block; margin-right: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background-color: #4472C4; color: white; font-weight: bold; padding: 10px; border: 1px solid #ddd; text-align: left; }
          td { padding: 8px; border: 1px solid #ddd; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .footer { margin-top: 30px; padding-top: 10px; border-top: 1px solid #ccc; font-size: 12px; color: #666; text-align: center; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="title">${title}</div>
          ${description ? `<div class="description">${description}</div>` : ''}
          ${metadata ? `
            <div class="metadata">
              ${Object.entries(metadata).map(([key, value]) => `
                <span class="metadata-item"><strong>${key}:</strong> ${value}</span>
              `).join('')}
            </div>
          ` : ''}
        </div>

        <table>
          <thead>
            <tr>
              ${columns.map(col => `<th>${col.header}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${rows.map(row => `
              <tr>
                ${columns.map(col => {
                  const value = row[col.key];
                  const formatted = col.format ? col.format(value) : value;
                  return `<td>${formatted ?? ''}</td>`;
                }).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="footer">
          Generated on ${new Date().toLocaleString()} | PIT Timetabling System
        </div>

        <script>
          window.onload = function() {
            window.print();
            setTimeout(function() { window.close(); }, 100);
          };
        </script>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  } catch (error) {
    console.error('Error exporting to PDF:', error);
    throw new Error('Failed to export to PDF');
  }
};

/**
 * Export report in the specified format
 */
export const exportReport = (
  data: ExportData,
  format: 'csv' | 'excel' | 'pdf',
  filename?: string
): void => {
  switch (format) {
    case 'csv':
      exportToCSV(data, filename);
      break;
    case 'excel':
      exportToExcel(data, filename);
      break;
    case 'pdf':
      exportToPDF(data, filename);
      break;
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Escape special characters for CSV
 */
function escapeCSV(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Sanitize filename to remove invalid characters
 */
function sanitizeFilename(filename: string): string {
  return filename
    .replace(/[^a-z0-9_\-]/gi, '_')
    .replace(/__+/g, '_')
    .toLowerCase();
}

/**
 * Get current timestamp for filenames
 */
function getTimestamp(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');

  return `${year}${month}${day}_${hours}${minutes}${seconds}`;
}

/**
 * Download file with given content
 */
function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();

  // Cleanup
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
}

// ============================================================================
// Common Export Configurations
// ============================================================================

/**
 * Common column formatters
 */
export const Formatters = {
  percentage: (value: number) => `${value}%`,
  decimal: (value: number, decimals: number = 2) => value.toFixed(decimals),
  currency: (value: number) => `$${value.toFixed(2)}`,
  date: (value: string | Date) => new Date(value).toLocaleDateString(),
  datetime: (value: string | Date) => new Date(value).toLocaleString(),
  boolean: (value: boolean) => value ? 'Yes' : 'No',
  yesNo: (value: any) => value ? 'Yes' : 'No',
  uppercase: (value: string) => value.toUpperCase(),
  capitalize: (value: string) => value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
};
